import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../../supabase'
import { useAuth } from '../../contexts/AuthContext'
import {
  aujourdhui, formatLong, nomMois, parseIso, jourSemaine, JOURS_COURTS, fetchAll,
} from '../../utils/planning'
import './MonPlanning.css'

// Un type visuel par nature de journee : accueil, supervision spa, supervision hydro, assistanat
const TYPES = {
  ACCUEIL: { label: 'Accueil', classe: 'accueil' },
  SUPERVISION: { label: 'Supervision Spa', classe: 'spa' },
  HYDRO: { label: 'Supervision Hydro', classe: 'hydro' },
  ASSISTANAT: { label: 'Assistanat de cours', classe: 'assistanat' },
}
const ORDRE = ['ACCUEIL', 'SUPERVISION', 'HYDRO', 'ASSISTANAT']

export default function MonPlanning() {
  const { profile } = useAuth()
  const [lignes, setLignes] = useState([])
  const [jours, setJours] = useState({})
  const [loading, setLoading] = useState(true)
  const [erreur, setErreur] = useState(false)
  const [passes, setPasses] = useState(false)
  const [ouverts, setOuverts] = useState({})

  const charger = useCallback(async () => {
    if (!profile) return
    setLoading(true)
    setErreur(false)
    // La securite (uniquement mes dates publiees) est assuree par les regles RLS ;
    // le filtre ci-dessous ne sert qu'a cibler la requete.
    const rA = await fetchAll(() => supabase.from('affectations')
      .select('id, date, type, statut, cours')
      .eq('superviseur_id', profile.id)
      .in('statut', ['prevu', 'valide'])
      .order('date').order('type'))
    if (rA.error) {
      setErreur(true)
      setLoading(false)
      return
    }
    const mJ = {}
    if (rA.data.length > 0) {
      const debut = rA.data[0].date
      const fin = rA.data[rA.data.length - 1].date
      const rJ = await fetchAll(() => supabase.from('jours_planning')
        .select('date, ferie, accueil_majore')
        .gte('date', debut).lte('date', fin).order('date'))
      for (const j of rJ.data) mJ[j.date] = j
    }
    setLignes(rA.data)
    setJours(mJ)
    setLoading(false)
  }, [profile])

  useEffect(() => { charger() }, [charger])

  const aujourd = aujourdhui()

  const { aVenir, ecoules } = useMemo(() => ({
    aVenir: lignes.filter(l => l.date >= aujourd),
    ecoules: lignes.filter(l => l.date < aujourd),
  }), [lignes, aujourd])

  const affichees = passes ? ecoules : aVenir

  // Regroupe par mois puis par jour (un jour peut cumuler plusieurs postes)
  const parMois = useMemo(() => {
    const groupes = []
    for (const l of affichees) {
      const { annee, mois } = parseIso(l.date)
      const cle = `${annee}-${mois}`
      let g = groupes[groupes.length - 1]
      if (!g || g.cle !== cle) {
        g = { cle, titre: `${nomMois(mois)} ${annee}`, jours: [] }
        groupes.push(g)
      }
      let j = g.jours[g.jours.length - 1]
      if (!j || j.date !== l.date) {
        j = { date: l.date, postes: [] }
        g.jours.push(j)
      }
      j.postes.push(l)
    }
    for (const g of groupes) {
      for (const j of g.jours) j.postes.sort((a, b) => ORDRE.indexOf(a.type) - ORDRE.indexOf(b.type))
    }
    return groupes
  }, [affichees])

  // Accordeon : le mois en cours est ouvert par defaut. S'il n'est pas dans la liste,
  // on ouvre le premier mois a venir, ou le dernier mois ecoule dans l'onglet "Passees".
  const cleParDefaut = useMemo(() => {
    const t = parseIso(aujourd)
    const courant = `${t.annee}-${t.mois}`
    if (parMois.some(g => g.cle === courant)) return courant
    if (parMois.length === 0) return null
    return passes ? parMois[parMois.length - 1].cle : parMois[0].cle
  }, [parMois, aujourd, passes])

  function estOuvert(cle) {
    return cle in ouverts ? ouverts[cle] : cle === cleParDefaut
  }

  function basculer(cle) {
    setOuverts(prev => ({ ...prev, [cle]: !estOuvert(cle) }))
  }

  const compteurs = useMemo(() => {
    const c = { ACCUEIL: 0, SUPERVISION: 0, HYDRO: 0, ASSISTANAT: 0 }
    for (const l of affichees) c[l.type] += 1
    return c
  }, [affichees])

  function detail(l) {
    if (l.type === 'ASSISTANAT') return l.cours ? `Cours : ${l.cours}` : ''
    const j = jours[l.date]
    if (!j || !j.ferie) return ''
    return l.type === 'ACCUEIL' && j.accueil_majore ? 'Jour férié, tarif majoré' : 'Jour férié'
  }

  const prochains = !passes && aVenir.length > 0 ? aVenir.filter(l => l.date === aVenir[0].date) : []

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Mon planning</h2>
          <div className="page-header-sub">Tes journées d'accueil, de supervision (spa et hydro) et d'assistanat</div>
        </div>
        <div className="tabs">
          <button className={`tab-btn ${!passes ? 'active' : ''}`} onClick={() => { setPasses(false); setOuverts({}) }}>À venir ({aVenir.length})</button>
          <button className={`tab-btn ${passes ? 'active' : ''}`} onClick={() => { setPasses(true); setOuverts({}) }}>Passées ({ecoules.length})</button>
        </div>
      </div>

      {erreur && <div className="alert alert-error">Impossible de charger ton planning.</div>}

      {loading ? <div className="spinner" /> : (
        <>
          {prochains.length > 0 && (
            <div className="stats-grid">
              <div className="stat-card">
                <div className="s-label">Prochaine journée</div>
                <div className="s-value" style={{ fontSize: 20 }}>{formatLong(prochains[0].date)}</div>
                <div className="s-sub">{prochains.map(l => TYPES[l.type].label).join(' + ')}</div>
              </div>
            </div>
          )}

          <div className="mp-legende">
            {ORDRE.map(t => (
              <div key={t} className="mp-compteur">
                <span className={`mp-point mp-point-${TYPES[t].classe}`} />
                {TYPES[t].label}
                <strong>{compteurs[t]}</strong>
              </div>
            ))}
          </div>

          {parMois.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', color: 'var(--stone)', padding: 32 }}>
              {passes
                ? 'Aucune journée passée.'
                : 'Aucune journée à venir dans le planning publié. Les dates apparaissent ici dès que la coordination les publie.'}
            </div>
          ) : parMois.map(g => {
            const ouvert = estOuvert(g.cle)
            const nb = g.jours.length
            return (
              <div className="mp-mois" key={g.cle}>
                <button
                  type="button"
                  className="mp-mois-entete"
                  aria-expanded={ouvert}
                  onClick={() => basculer(g.cle)}
                >
                  <span className="mp-mois-titre">{g.titre}</span>
                  <span className="mp-mois-nb">{nb} journée{nb > 1 ? 's' : ''}</span>
                  <span className={`mp-chevron ${ouvert ? 'mp-chevron-ouvert' : ''}`} aria-hidden="true">▾</span>
                </button>
                {ouvert && g.jours.map(j => (
                  <div key={j.date} className={`mp-jour ${j.date === aujourd ? 'mp-jour-aujourdhui' : ''}`}>
                    <div className="mp-date">
                      <div className="mp-date-sem">{JOURS_COURTS[jourSemaine(j.date)]}</div>
                      <div className="mp-date-num">{parseIso(j.date).jour}</div>
                    </div>
                    <div className="mp-postes">
                      {j.postes.map(l => (
                        <div key={l.id} className={`mp-poste mp-${TYPES[l.type].classe}`}>
                          <span className="mp-poste-label">{TYPES[l.type].label}</span>
                          {detail(l) && <span className="mp-poste-detail">{detail(l)}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )
          })}
        </>
      )}
    </div>
  )
}
